import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid, { FormGridFullWidth, FormGridSection } from '../../common/FormGrid';
import { FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaCheck, FaSearch, FaUser, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt, FaIdCard } from 'react-icons/fa';
import './CatalogoProveedores.css';
import { getApiUrl } from '../../../utils/api';

const CatalogoProveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Ya no necesitamos modo edición, siempre se puede editar
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proveedorAEliminar, setProveedorAEliminar] = useState(null);
  const toast = useToast();

  // Estado del formulario
  const [formulario, setFormulario] = useState({
    nombre: '',
    razonSocial: '',
    contacto: '',
    rfc: '',
    codigoPostal: '',
    calle: '',
    numero: '',
    colonia: '',
    ciudad: '',
    estado: '',
    telefono1: '',
    telefono2: '',
    correo: ''
  });

  // Cargar proveedores al montar
  useEffect(() => {
    cargarProveedores();
  }, []);

  const cargarProveedores = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getProveedoresInventario' })
      });
      const data = await response.json();
      if (data.success) {
        setProveedores(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar proveedores');
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      toast.error('Error al cargar los proveedores');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulario({
      ...formulario,
      [name]: value
    });
  };

  // Abrir modal para nuevo proveedor
  const nuevoProveedor = () => {
    setProveedorSeleccionado(null);
    setFormulario({
      nombre: '',
      razonSocial: '',
      contacto: '',
      rfc: '',
      codigoPostal: '',
      calle: '',
      numero: '',
      colonia: '',
      ciudad: '',
      estado: '',
      telefono1: '',
      telefono2: '',
      correo: ''
    });
    setShowModal(true);
  };

  // Limpiar formulario y cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setProveedorSeleccionado(null);
    setFormulario({
      nombre: '',
      razonSocial: '',
      contacto: '',
      rfc: '',
      codigoPostal: '',
      calle: '',
      numero: '',
      colonia: '',
      ciudad: '',
      estado: '',
      telefono1: '',
      telefono2: '',
      correo: ''
    });
  };

  // Manejar teclas de acceso rápido
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        guardarProveedor();
      }
      if (e.key === 'Escape') {
        if (showModal) {
          handleCloseModal();
        }
      }
      if (e.key === 'Delete' && proveedorSeleccionado) {
        confirmarEliminarProveedor(proveedorSeleccionado);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [proveedorSeleccionado, formulario]);

  // Filtrar proveedores basado en la búsqueda
  const proveedoresFiltrados = proveedores.filter(proveedor => {
    if (!busqueda) return true;

    const textoBusqueda = busqueda.toLowerCase();
    return (
      proveedor.nombre?.toLowerCase().includes(textoBusqueda) ||
      proveedor.razonSocial?.toLowerCase().includes(textoBusqueda) ||
      proveedor.contacto?.toLowerCase().includes(textoBusqueda) ||
      proveedor.rfc?.toLowerCase().includes(textoBusqueda) ||
      proveedor.calle?.toLowerCase().includes(textoBusqueda) ||
      proveedor.colonia?.toLowerCase().includes(textoBusqueda) ||
      proveedor.ciudad?.toLowerCase().includes(textoBusqueda) ||
      proveedor.estado?.toLowerCase().includes(textoBusqueda) ||
      proveedor.telefono1?.toLowerCase().includes(textoBusqueda) ||
      proveedor.correo?.toLowerCase().includes(textoBusqueda)
    );
  });

  // Seleccionar proveedor (solo para resaltar visualmente)
  const seleccionarProveedor = (proveedor) => {
    setProveedorSeleccionado(proveedor);
  };

  // Editar proveedor en modal
  const editarProveedor = (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setFormulario({
      nombre: proveedor.nombre || '',
      razonSocial: proveedor.razonSocial || '',
      contacto: proveedor.contacto || '',
      rfc: proveedor.rfc || '',
      codigoPostal: proveedor.codigoPostal || '',
      calle: proveedor.calle || '',
      numero: proveedor.numero || '',
      colonia: proveedor.colonia || '',
      ciudad: proveedor.ciudad || '',
      estado: proveedor.estado || '',
      telefono1: proveedor.telefono1 || '',
      telefono2: proveedor.telefono2 || '',
      correo: proveedor.correo || ''
    });
    setShowModal(true);
  };

  // Guardar proveedor
  const guardarProveedor = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre del proveedor es requerido');
      return;
    }

    try {
      setGuardando(true);

      const esEdicion = !!proveedorSeleccionado;
      const payload = {
        tipo: esEdicion ? 'actualizarProveedor' : 'crearProveedor',
        ...formulario
      };

      if (esEdicion) {
        payload.idProveedor = proveedorSeleccionado.idProveedor;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente');
        handleCloseModal();
        cargarProveedores();
      } else {
        toast.error(data.message || 'Error al guardar el proveedor');
      }

    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  // Confirmar eliminar proveedor
  const confirmarEliminarProveedor = (proveedor) => {
    setProveedorAEliminar(proveedor);
    setShowDeleteModal(true);
  };

  // Eliminar proveedor
  const eliminarProveedor = async () => {
    if (!proveedorAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarProveedor',
          idProveedor: proveedorAEliminar.idProveedor
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Proveedor eliminado correctamente');
        setShowDeleteModal(false);
        setProveedorAEliminar(null);
        setProveedorSeleccionado(null);
        cargarProveedores();
      } else {
        toast.error(data.message || 'Error al eliminar el proveedor');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  // Cerrar modal de confirmación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setProveedorAEliminar(null);
  };

  return (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar proveedores..."
                className="excel-search-input"
                title="Buscar por nombre, RFC, contacto, ciudad, etc."
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="excel-clear-search"
                  title="Limpiar búsqueda"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button
              onClick={nuevoProveedor}
              className="btn-excel btn-nuevo"
              title="Nuevo proveedor"
            >
              <FaPlus /> Nuevo Proveedor
            </button>
          </div>
        </div>
      </div>


      {/* Tabla estilo Excel */}
      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">Nombre</th>
              <th className="excel-th">Razón Social</th>
              <th className="excel-th">Contacto</th>
              <th className="excel-th">RFC</th>
              <th className="excel-th">Dirección</th>
              <th className="excel-th">Ciudad</th>
              <th className="excel-th">Estado</th>
              <th className="excel-th">CP</th>
              <th className="excel-th">Teléfono</th>
              <th className="excel-th">Correo</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="excel-loading">Cargando proveedores...</td>
              </tr>
            ) : proveedoresFiltrados.length === 0 ? (
              <tr>
                <td colSpan="11" className="excel-empty">
                  {busqueda ? `No se encontraron proveedores que coincidan con "${busqueda}"` : 'No hay proveedores registrados'}
                </td>
              </tr>
            ) : (
              proveedoresFiltrados.map(proveedor => (
                <tr
                  key={proveedor.idProveedor}
                  className={`excel-row ${
                    proveedorSeleccionado?.idProveedor === proveedor.idProveedor ? 'excel-row-selected' : ''
                  }`}
                  onClick={() => seleccionarProveedor(proveedor)}
                >
                  <td className="excel-cell excel-cell-nombre">{proveedor.nombre}</td>
                  <td className="excel-cell">{proveedor.razonSocial}</td>
                  <td className="excel-cell">{proveedor.contacto}</td>
                  <td className="excel-cell excel-cell-small">{proveedor.rfc}</td>
                  <td className="excel-cell">
                    {proveedor.calle} {proveedor.numero} {proveedor.colonia}
                  </td>
                  <td className="excel-cell">{proveedor.ciudad}</td>
                  <td className="excel-cell excel-cell-small">{proveedor.estado}</td>
                  <td className="excel-cell excel-cell-xsmall">{proveedor.codigoPostal}</td>
                  <td className="excel-cell">{proveedor.telefono1}</td>
                  <td className="excel-cell">{proveedor.correo}</td>
                  <td className="excel-cell excel-cell-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editarProveedor(proveedor);
                      }}
                      className="excel-btn-action excel-btn-edit"
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarEliminarProveedor(proveedor);
                      }}
                      className="excel-btn-action excel-btn-delete"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer con información */}
      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de registros: <strong>{proveedores.length}</strong></span>
          {busqueda && (
            <span>| Filtrados: <strong>{proveedoresFiltrados.length}</strong></span>
          )}
          {proveedorSeleccionado && (
            <span>| Registro seleccionado: <strong>{proveedorSeleccionado.nombre}</strong></span>
          )}
        </div>
        <div className="excel-shortcuts">
          <span>Nuevo: Crear proveedor | Enter: Editar | Del: Eliminar</span>
        </div>
      </div>

      {/* Modal para crear/editar proveedor */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={proveedorSeleccionado ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        onSave={guardarProveedor}
        saveLabel={proveedorSeleccionado ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="medium"
        saveIcon={<FaBuilding />}
        maxHeight="45vh"
      >
        <FormGrid columns={2} gap="10px">
          <FormField
            label="Nombre"
            name="nombre"
            value={formulario.nombre}
            onChange={handleInputChange}
            placeholder="Nombre del proveedor"
            required
            icon={<FaBuilding />}
          />

          <FormField
            label="Contacto"
            name="contacto"
            value={formulario.contacto}
            onChange={handleInputChange}
            placeholder="Persona de contacto"
            icon={<FaUser />}
          />

          <FormField
            label="Teléfono"
            type="tel"
            name="telefono1"
            value={formulario.telefono1}
            onChange={handleInputChange}
            placeholder="Teléfono principal"
            icon={<FaPhone />}
          />

          <FormField
            label="Correo"
            type="email"
            name="correo"
            value={formulario.correo}
            onChange={handleInputChange}
            placeholder="correo@ejemplo.com"
            icon={<FaEnvelope />}
          />

          <FormField
            label="RFC"
            name="rfc"
            value={formulario.rfc}
            onChange={handleInputChange}
            placeholder="RFC del proveedor"
            icon={<FaIdCard />}
          />

          <FormField
            label="Ciudad"
            name="ciudad"
            value={formulario.ciudad}
            onChange={handleInputChange}
            placeholder="Ciudad"
            icon={<FaMapMarkerAlt />}
          />
        </FormGrid>

        <FormGridFullWidth>
          <FormField
            label="Razón Social"
            name="razonSocial"
            value={formulario.razonSocial}
            onChange={handleInputChange}
            placeholder="Razón social completa (opcional)"
            icon={<FaBuilding />}
          />
        </FormGridFullWidth>
      </FormModal>

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarProveedor}
        title="Eliminar Proveedor"
        message={`¿Está seguro de eliminar al proveedor "${proveedorAEliminar?.nombre}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />
    </div>
  );
};

export default CatalogoProveedores;